import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react'
import {
  Button,
  BUTTON_SIZE,
  BUTTON_TYPE,
  NaverMap,
  GoToTop,
  MapMarker,
  MarkerType,
  styled,
  Toolbar,
  useGoToTop,
  Retry,
} from '~/app/components'
import ClipboardJS from 'clipboard'
import CancelPolicy from '~/app/components/order-list/common/CancelPolicy'
import QueryString from 'qs'
import conf from '~/app/conf'
import useToolbarScroll from '~/app/components/common/Toolbar/useToolbarScroll'
import { CategoryOrderIdContainer } from '~/app/components/order-list/details/OrderDetailStyle'
import { CategoryTypeText } from '~/app/components/order-list/common/OrderCardItem'
import { TOrderDetailsLeisureInformation } from '~/app/components/order-list/hooks/useLeisureInfo'
import LeisureDetailInfo from '~/app/components/common/Leisure/LeisureDetailInfo'
import { CategoryType, RefundApprovalTypeCode } from '~/__generated__/__types__'
import { Toast, ToastHandler } from '@yanolja-org/ya-com-toast'
import { Typography } from '@yanolja-org/ya-com-core'
import { Icon_location_fill } from '@yanolja-org/ya-com-system-icon'
import { Layout, ContentContainer, Content } from '~/app/components/layout-style'
import { Detail } from '~/app/components/order-list/common/OrderDetailsCommonStyled'
import { isNotEmpty } from '~/app/utils'

/**
 * LeisureInformation component
 * author: soojin.oh
 */

// type definitions
type Props = {
  loading: boolean
  leisureInformation?: TOrderDetailsLeisureInformation
  onBackClick: () => void
  categoryOrderId: string
  categoryOrderType: CategoryType
  refetchOrderDetails: () => void
}

// styled components
const MapFrame = styled.div`
  height: 15rem;
  border-radius: 0.8rem;
  overflow: hidden;
  margin-bottom: 1rem;
`

const Information = styled.div`
  padding: 0 2rem;
  & .title {
    ${Typography('subtitle3').bold};
    color: ${({ theme }) => theme.color.gray900};
    padding: 1.5rem 0 1.4rem;
    &.sub {
      ${Typography('body1').bold};
      padding: 0;
      margin-bottom: 1.2rem;
    }
  }
`
const LocationIcon = styled(Icon_location_fill)`
  margin-right: 0.4rem;
`

// LeisureInformation component
const LeisureInformation: FC<Props> = function ({
  loading,
  leisureInformation,
  onBackClick,
  categoryOrderId,
  categoryOrderType,
  refetchOrderDetails,
}) {
  const toastRef = useRef<ToastHandler>()
  const triggerToast = useCallback(() => {
    toastRef.current?.addMessage({ message: '주소를 복사했어요', timeout: 3000 })
  }, [])

  const scrollTarget = useRef<HTMLDivElement>(null)
  const { isFabOpen, onClick } = useGoToTop(scrollTarget)
  const { hasShadow } = useToolbarScroll(scrollTarget)

  const cancelPolicyUrl = useMemo(() => {
    if (!leisureInformation) return
    const hasPenalty = leisureInformation.product.isCancelPenalty
    const refundApprovalTypeCode = leisureInformation.variant.refundApprovalTypeCode
    const refundType = refundApprovalTypeCode
      ? refundApprovalTypeCode === RefundApprovalTypeCode.Direct
        ? 'REFUND_NOW'
        : 'REFUND_IN_7D'
      : null
    const queryString = QueryString.stringify(
      {
        items: [
          {
            name: leisureInformation.dealName,
            hasPenalty,
            refundType,
          },
        ],
        categoryName:
          CategoryTypeText[categoryOrderType] === '레저'
            ? '국내레저'
            : CategoryTypeText[categoryOrderType],
        embed: true,
      },
      { encodeValuesOnly: true }
    )
    return `${conf.policy.url}/pf/ticket/cancel?${queryString}`
  }, [leisureInformation, categoryOrderType])

  const { address, latitude, longitude, facilityInfo } = useMemo(() => {
    return (
      leisureInformation?.product.facilities.find(facility => facility.isRepFacility) ?? {
        address: '',
        latitude: 0,
        longitude: 0,
        facilityInfo: [],
      }
    )
  }, [leisureInformation])

  const markers = useMemo(
    () => [<MapMarker key={0} type={MarkerType.LEISURE} lat={latitude} lng={longitude} />],
    [latitude, longitude]
  )

  const refundInfo = leisureInformation?.product.productInfo?.refundInfo ?? []

  useEffect(() => {
    const clipboard = new ClipboardJS('.link')
    clipboard.on('success', triggerToast)
  }, [])

  return (
    <Layout>
      <Toolbar
        title={'이용정보'}
        type={'close'}
        onClickBackButton={onBackClick}
        hasShadow={hasShadow}
      />
      <ContentContainer>
        <Content
          ref={scrollTarget}
          style={{
            background: 'white',
            justifyContent: 'flex-start',
            padding: '1.3rem 0 6.4rem',
          }}
        >
          <CategoryOrderIdContainer
            style={{
              margin: '-12px -20px 12px -20px',
            }}
          >
            <div>
              {CategoryTypeText[categoryOrderType]} 예약번호 {categoryOrderId}
            </div>
          </CategoryOrderIdContainer>
          {!!leisureInformation ? (
            <>
              <Information>
                <div className={'title'}>업체 정보</div>
                <MapFrame>
                  <NaverMap zoom={15} lat={latitude} lng={longitude} markers={markers} />
                </MapFrame>
                <Detail style={{ margin: '0 0 1rem' }}>
                  <LocationIcon size={1.4} unit="rem" />
                  <div className={'description'}>{address}</div>
                  <Button
                    type={BUTTON_TYPE.BLUE}
                    size={BUTTON_SIZE.M}
                    title={'주소복사'}
                    className={'link'}
                    dataClipboardText={address || ''}
                    style={{
                      border: 'none',
                      borderRadius: 0,
                      marginTop: '-1rem',
                      padding: '1rem 0',
                    }}
                  >
                    주소복사
                  </Button>
                </Detail>
                <LeisureDetailInfo infos={facilityInfo ?? []} level={0} />
              </Information>
              <Information>
                <div className={'title'}>이용 상세 정보</div>
                <LeisureDetailInfo
                  infos={leisureInformation?.product.productInfo?.usageInfo ?? []}
                  level={0}
                />
                {isNotEmpty(refundInfo) && (
                  <>
                    <div className={'title sub'}>환불 정책</div>
                    <LeisureDetailInfo infos={refundInfo} level={0} />
                  </>
                )}
              </Information>
              <CancelPolicy id={'cancel-policy'} cancelPolicyUrl={cancelPolicyUrl} />
            </>
          ) : loading ? (
            <></>
          ) : (
            <Retry hasError={!leisureInformation} onRetryClick={refetchOrderDetails} />
          )}
        </Content>
      </ContentContainer>
      <GoToTop isOpened={isFabOpen} onClick={onClick} />
      <Toast toastRef={toastRef} />
    </Layout>
  )
}

export default LeisureInformation
